import Cookies from "js-cookie"
import { html, nothing } from "lit-html"
import { virtual, useEffect, useState } from "haunted"
import { ROLE_ICON_MAP } from "../constants"
import { parseDate } from "../utils"
import { SelectBox } from "../utils/select-box"
import { FILTER_BY_DATE_OPTIONS, FILTER_BY_STATUS_OPTIONS } from "./constants"

const slugify = (text) => {
  return text.toLowerCase().replace(" ", "-")
}

export const PerformanceComponent = virtual(({ endpoint }) => {
  const [loading, setLoading] = useState(true)
  const [assignmentList, setAssignmentList] = useState([])
  const [assignmentLists, setAssignmentLists] = useState({})
  const [assignmentCount, setAssignmentCount] = useState(-1)
  const [queryStr, setQueryStr] = useState("")
  const [status, setStatus] = useState(null)
  const csrftoken = Cookies.get("csrftoken")
  const $profileImgSrc = document.querySelector("#profile-img-src")

  const loadPerformance = () => {
    setLoading(true)
    if (queryStr in assignmentLists) {
      setAssignmentList(assignmentLists[queryStr])
      setTimeout(() => setLoading(false), 100)
      return
    }

    fetch(`${endpoint}?${queryStr}`, {
      method: "GET",
      credentials: "include",
      headers: {
        "X-CSRFToken": csrftoken,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((res) => {
        let assignments = []
        res.forEach((assignmentWithRoles) => {
          const { roles, ...assignment } = assignmentWithRoles
          assignments = assignments.concat(
            ...roles.map((role) => ({ role, ...assignment }))
          )
        })

        if (assignmentCount === -1) {
          setAssignmentCount(assignments.length)
        }
        setAssignmentList(assignments)
        setAssignmentLists({
          [queryStr]: assignments,
          ...assignmentLists,
        })
        setLoading(false)
      })
      .catch((err) => console.error(err))
  }

  useEffect(loadPerformance, [queryStr])

  const RoleIcon = (category, url) => {
    return html`
      <a
        class="role-icon-link hover:border-none mr-2 ${category}"
        href="${url}"
      >
        <i
          class=${category in ROLE_ICON_MAP
            ? ROLE_ICON_MAP[category]
            : "fas fa-pen-alt"}
        >
        </i>
      </a>
    `
  }

  const AssignmentByRoleRow = ({ id, name, url, dates, tags, role }) => {
    const {
      category,
      url: roleUrl,
      status: roleStatus,
      rating,
      payment_info: paymentInfo,
    } = role
    const meetingDate = dates.meeting ? parseDate(dates.meeting) : null
    const releasedDate = dates.assignment ? parseDate(dates.assignment) : null
    const dateDisplayOptions = {
      year: "numeric",
      month: "short",
      day: "numeric",
    }

    const hiddenClass = status === null || roleStatus === status ? "" : "hidden"
    return html`
      <div
        id="assignment-${id}-${category}"
        ,
        class="${hiddenClass} performance-row p-7 pb-8half flex flex-row flex-wrap text-18"
      >
        <div class="assignment-column">
          <div class="mb-5 text-sm text-tag-text-yellow">${tags}</div>
          <div class="mb-5 flex flex-row">${RoleIcon(category, roleUrl)}</div>
          <div>
            <a class="font-bold text-blue" href="${url}">${name}</a>
            <div class="mt-3 text-sm text-gray-600">
              ${meetingDate
                ? "Meeting date: " +
                  meetingDate.toLocaleDateString("en-US", dateDisplayOptions)
                : releasedDate
                ? "Assignment posted: " +
                  releasedDate.toLocaleString("en-US", dateDisplayOptions)
                : ""}
            </div>
            <div class="flex">
              ${paymentInfo.in_person_rate
                ? html`<div class="mt-4 mr-5 flex">
                    <img
                      class="mr-2 w-6"
                      src="${$profileImgSrc.dataset.iconInPersonHourlyRate}"
                      alt="In-person hourly rate"
                    />
                    <span class="text-sm"
                      >$${paymentInfo.in_person_rate}/hr</span
                    >
                  </div>`
                : nothing}
              ${paymentInfo.remote_rate
                ? html`<div class="mt-4 mr-5 flex">
                    <img
                      class="mr-2 w-6"
                      src="${$profileImgSrc.dataset.iconRemoteHourlyRate}"
                      alt="Remote hourly rate"
                    />
                    <span class="text-sm">$${paymentInfo.remote_rate}/hr</span>
                  </div>`
                : nothing}
            </div>
          </div>
          <div class="md:hidden mt-5 text-sm sm:text-base">
            <span class="text-gray-600">Status: </span>
            <span
              class="text-role-status text-role-status-${slugify(roleStatus)}"
              >${roleStatus}</span
            >
          </div>
        </div>
        <div
          class="status-column hidden md:flex flex-row items-center text-role-status text-role-status-${slugify(
            roleStatus
          )}"
        >
          ${roleStatus}
        </div>
        <div
          class="rating-column flex flex-row items-center justify-center font-bold"
        >
          ${rating ? rating : "--"}
        </div>
      </div>
    `
  }

  const hasAssignmentOfStatus =
    status === null ||
    assignmentList.some((assignment) => assignment.role.status === status)
  const content = () => {
    if (hasAssignmentOfStatus) {
      return html`
        <div
          class="mt-6 px-7 pt-8 pb-6 bg-gray-bg flex flex-row flex-wrap text-lg"
        >
          <div class="assignment-column">Assignments</div>
          <div class="status-column hidden md:flex">Status</div>
          <div class="rating-column text-center">Rating</div>
        </div>
        ${assignmentList.map((assignment) => AssignmentByRoleRow(assignment))}
      `
    } else {
      return html`
        <div class="my-10 flex flex-col items-center justify-center h-full">
          No results.
        </div>
      `
    }
  }

  return assignmentCount > 0
    ? html`
        <div
          class="section-box pt-8 text-black loader-container ${loading
            ? "loading"
            : ""}"
        >
          <div class="px-7 flex flex-row justify-between flex-wrap">
            <h2 class="mr-7 font-serif text-sm">Assignment Performance</h2>
            <div class="filters flex flex-row flex-wrap">
              <div class="mr-5 mb-6">
                ${SelectBox({
                  width: "175px",
                  options: FILTER_BY_DATE_OPTIONS.map(
                    ({ label, queryStr }) => ({
                      label,
                      onClick: () => setQueryStr(queryStr),
                    })
                  ),
                })}
              </div>
              ${SelectBox({
                width: "215px",
                options: FILTER_BY_STATUS_OPTIONS.map((label) => ({
                  label,
                  onClick: () => setStatus(label),
                })),
                placeholder: "Filter by status",
                resetOption: {
                  label: "Clear filter",
                  onClick: () => setStatus(null),
                },
              })}
            </div>
          </div>
          ${loading ? "" : content()}
        </div>
      `
    : html``
})
